import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { RootState, AppDispatch } from "../../store";
import useApi from "../../services/useApi";
import EmptyListingScreen from "../emptyScreens/EmptyListingScreen";
import useTranslation from "../CustomHooks/useTranslation";
import { breadcrumbsContent } from "../breadcrumbs/BreadcrumbsContent";
import { setBalanceSheetData, resetBalanceSheetState } from "../../store/slices/balanceSheetSlice";
import DynamicFilterPanel, { DynamicFilterConfig } from "../DynamicComponent/DynamicFilterPanel";
import ButtonWithImg from "../buttons/ButtonWithImg";
import { showToast } from "../toastMessage/toastSlice";
import BalanceSheetShimmer from "./BalanceSheetShimmer";

interface BalanceSheetItem {
  name: string;
  balance: number;
}

interface BalanceSheetSection {
  details: BalanceSheetItem[];
  total: number;
}

interface BalanceSheetData {
  assets?: BalanceSheetSection;
  liabilities?: BalanceSheetSection;
  equity_capital?: BalanceSheetSection;
  revenue?: BalanceSheetSection;
  expenses?: BalanceSheetSection;
  net_income?: number;
  total_amount?: number;
}

interface MainTableProps {
  staticText: {
    emptyScreenText: {
      heading1: string;
      heading2: string;
    };
  };
  mainEndPoint: string;

  params?: { [key: string]: string };
  searchPlaceholder?: string;
  filters: boolean;
  exporting: boolean;
  dynamicFilterConfig?: DynamicFilterConfig;
  addMorePadding?: boolean;
  customHeight?: string;
}

// Helper function to format AFA amounts
function formatAfaAmount(amount: number | string | undefined | null) {
  const num = Number(amount || "0.00");
  if (isNaN(num)) return "AFA 0.00";
  if (num < 0) {
    return `AFA (-) ${Math.abs(num).toFixed(2)}`;
  }
  return `AFA ${num.toFixed(2)}`;
}

// Helper function to format date from yyyy-mm-dd to dd-mm-yyyy if needed
function formatDisplayDate(dateStr: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (match) {
    return `${match[3]}-${match[2]}-${match[1]}`;
  }
  return dateStr;
}

export default function BalanceSheet({
  mainEndPoint,
  params = {},
  filters,
  exporting,
  dynamicFilterConfig,
  addMorePadding,
  customHeight
}: MainTableProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, data: apiData } = useSelector((state: RootState) => state.balanceSheet as { loading: boolean; error: any; data: BalanceSheetData | null });
  const { translate, notEnglish } = useTranslation(breadcrumbsContent);
  const filersWithEmpty = {
    heading1: translate("No results found"),
    heading2: translate(""),
    img: "search_empty_screen_img",
  };
  const [searchParams, setSearchParams] = useSearchParams(params);
  const { getAPI } = useApi();
  const [isExportOpen, setIsExportOpen] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [filteredDate, setFilteredDate] = useState<string>('');

  // Filter logic
  const handleFilter = (filters: { [key: string]: any } | null) => {
    setSearchParams((prev) => {
      const updatedParams = new URLSearchParams(prev);
      // updatedParams.set("page_number", "1");
      if (filters === null && dynamicFilterConfig) {
        Object.values(dynamicFilterConfig).forEach((conf: any) => {
          if (conf.type === "date" && typeof conf.searchKey === "object") {
            updatedParams.delete(conf.searchKey.start);
            updatedParams.delete(conf.searchKey.end);
          } else if (typeof conf.searchKey === "string") {
            updatedParams.delete(conf.searchKey);
          }
        });
        return updatedParams;
      }
      if (filters) {
        Object.keys(filters).forEach((key) => {
          let value = filters[key];
          if (value && typeof value === "object" && typeof (value as any).format === "function") {
            // Handle dayjs objects directly
            updatedParams.set(key, (value as any).format("YYYY-MM-DD"));
          } else if (value !== null && value !== "" && value !== undefined) {
            updatedParams.set(key, String(value));
          } else {
            updatedParams.delete(key);
          }
        });
      }
      return updatedParams;
    });
  };

  // Export logic
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        exportRef.current &&
        !exportRef.current.contains(event.target as Node)
      ) {
        setIsExportOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Extract data for rendering
  const balanceData: BalanceSheetData = apiData || {};

  const exportOptions = [
    { label: "PDF", value: "pdf", textColorClass: "text-s_dark_hover" },
    { label: "EXCEL", value: "xlsx", textColorClass: "text-positive" },

  ];

  const handleExportOptionClick = async (value: string): Promise<void> => {
    setIsExportOpen(false);
    try {
      setExportLoading(true);
      let res;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const paramString = new URLSearchParams(Object.fromEntries(searchParams.entries())).toString();
      const endpoint = `${mainEndPoint}?${paramString}`;
      res = await getAPI(`${endpoint}&export_format=${value}&client_timezone=${timezone}`);
      if (!res.error) {
        if (res.data.url) {
          window.open(res.data.url, "_blank");
          dispatch(
            showToast({
              type: "success",
              message: translate("List exported successfully"),
            })
          );
        }
      } else {
        dispatch(
          showToast({
            type: "error",
            message: "something went wrong",
          })
        );
      }
    } catch (error) {
      dispatch(
        showToast({
          type: "error",
          message: "something went wrong",
        })
      );
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      dispatch(resetBalanceSheetState());
      // Remove page_number from params before making API call
      const paramsObj = Object.fromEntries(searchParams.entries());
      delete paramsObj.page_number;
      const paramString = new URLSearchParams(paramsObj).toString();
      setFilteredDate(paramString);
      const endpoint = `${mainEndPoint}${paramString ? `?${paramString}` : ""}`;
      const response = await getAPI(endpoint);
      dispatch(setBalanceSheetData(response));
    };
    fetchData();
    // eslint-disable-next-line
  }, [searchParams, mainEndPoint]);

  // Layout constants
  const sectionClass = "bg-p_light text-heading5-semi text-[#4B5563] py-[2px]";
  const cellClass = "px-4 py-2 text-body2 text-[#1f1f1f]";
  const totalClass =
    "px-4 py-2 text-large text-p_primary flex justify-between";
  const colHeaderClass = "text-title1 text-black  px-4 py-2";
  const currentDate = searchParams.size !== 0 ? (searchParams.get('end_date')?.split('-').join('-') || '') : '';
  return (
    <div
      className={`relative h-maintableheight2 mt-1.5 bg-white rounded-lg`}
    >
      {loading ? (
        <BalanceSheetShimmer />
      ) : (!apiData || Object.keys(apiData || {}).length === 0) ? (
        <div className="flex items-center justify-center h-emptyscreenheight absolute inset-0 bg-white bg-opacity-80 z-30">
          <EmptyListingScreen emptyScreenText={filersWithEmpty} />
        </div>
      ) : (
        <>
          <div className="sticky top-0 z-20 bg-white rounded-t-lg flex justify-between items-center pt-4 pb-1 pr-6 pl-[23px]">
            <h1 className="text-heading4-semi">
              Balance Sheet as of{" "}
              {currentDate !== '' ? formatDisplayDate(currentDate || '') : new Date()
                .toLocaleDateString("en-GB")
                .replace(/\//g, "-")}
            </h1>
            <div className="flex gap-x-3 items-center justify-center">
              {filters && dynamicFilterConfig && (
                <DynamicFilterPanel
                  config={dynamicFilterConfig}
                  onApply={handleFilter}
                  onClear={() => handleFilter(null)}
                  initialValues={{}}
                  className=""
                  customHeight={customHeight}
                  searchParams={searchParams}
                  balanceSheet={true}
                />
              )}
              <div className="relative" ref={exportRef}>
                {exporting && (
                  <ButtonWithImg
                    buttonName={translate("Export")}
                    disabledButton={
                      loading ||
                      !balanceData?.assets ||
                      !balanceData.assets.details ||
                      balanceData.assets.details.length === 0
                    }
                    ImgSrc={
                      loading ||
                        !balanceData?.assets ||
                        !balanceData.assets.details ||
                        balanceData.assets.details.length === 0
                        ? "loading-export"
                        : "export"
                    }
                    testId="export"
                    className={`px-4 bg-p_primary text-white ${notEnglish ? "w-fit" : "w-[110px]"
                      }`}
                    buttonOnclick={() => setIsExportOpen(!isExportOpen)}
                    loading={exportLoading}
                  />
                )}
                {isExportOpen && (
                  <div className="absolute right-0 mt-1 w-[110px] bg-white border border-s_dark_hover rounded-lg z-[11]">
                    <ul className="flex flex-col">
                      {exportOptions.map((option) => (
                        <li
                          key={option.value}
                          data-testid={`${option.value}`}
                          className={`p-2 text-body2 cursor-pointer ${option.textColorClass} hover:bg-p_light rounded-lg`}
                          onClick={() => handleExportOptionClick(option.value)}
                        >
                          {option.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="overflow-y-auto ml-[23px] mr-[15px] mt-2 pr-2 scrollBar h-addsreenheight">
            <div>
              <div className=" ">
                <div className="flex w-full gap-8">
                  {/* Assets */}
                  <div className="w-1/2">
                    <div className="bg-white">
                      <div className={`${sectionClass} flex justify-between`}>
                        <div className={colHeaderClass}>Assets</div>
                        <div className={colHeaderClass}>Amount</div>
                      </div>
                      {balanceData?.assets?.details?.map((item: BalanceSheetItem, idx: number) => (
                        <div className="flex justify-between py-1" key={idx}>
                          <div className={cellClass}><Link
                            to={`/zoodpay-money/balance-sheet/${item.name.toLowerCase().replace(/[()]/g, "").replace(/\s+/g, "-").replace(/\//g, "-")}${searchParams.size !== 0 ? `?${searchParams}` : ""}`} className="hover:text-p_primary hover:underline"
                          >
                            {item.name}
                          </Link></div>
                          <div className={cellClass} data-testid={item.name}>
                            <Link
                              to={`/zoodpay-money/balance-sheet/${item.name.toLowerCase().replace(/[()]/g, "").replace(/\s+/g, "-").replace(/\//g, "-")}${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                              className="hover:text-p_primary hover:underline"
                            >
                              {formatAfaAmount(item.balance)}
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Liabilities */}
                  <div className="w-1/2">
                    <div className="bg-white">
                      <div className={`${sectionClass} flex justify-between`}>
                        <div className={colHeaderClass}>Liabilities</div>
                        <div className={colHeaderClass}>Amount</div>
                      </div>
                      {balanceData.liabilities?.details?.map(
                        (item: BalanceSheetItem, idx: number) => (
                          <div className="flex justify-between py-1" key={idx}>
                            {
                            item.name === "Recovery Payable" ? (
                              <>
                                <div className={cellClass} >
                                  <Link
                                    to={`/zoodpay-money/balance-sheet/recovery-payable/transactions${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                    className="hover:text-p_primary hover:underline"
                                  >
                                    {item.name}
                                  </Link>
                                </div>
                                <div className={cellClass} data-testid={item.name}>
                                  <Link
                                    to={`/zoodpay-money/balance-sheet/recovery-payable/transactions${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                    className="hover:text-p_primary hover:underline"
                                  >
                                    {formatAfaAmount(item.balance)}
                                  </Link>
                                </div>
                              </>
                            ) : item.name === "Customer Stock GL" ? (
                              <>
                                <div className={cellClass}>
                                  <Link
                                    to={`/zoodpay-money/balance-sheet/customer-stock/transactions${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                    className="hover:text-p_primary hover:underline"
                                  >
                                    {item.name}
                                  </Link>
                                </div>
                                <div className={cellClass} data-testid={item.name}>
                                  <Link
                                    to={`/zoodpay-money/balance-sheet/customer-stock/transactions${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                    className="hover:text-p_primary hover:underline"
                                  >
                                    {formatAfaAmount(item.balance)}
                                  </Link>
                                </div>
                              </>
                            ) : item.name === "ZoodPay/Parent Payable" || item.name === "Etopup MNO Payable GL" || item.name === "Bundle Activation MNO Payable GL" ? (
                              <>
                                <div className={cellClass}>
                                  <Link
                                    to={`/zoodpay-money/balance-sheet/${item.name.toLowerCase().replace(/[()]/g, "").replace(/\s+/g, "-").replace(/\//g, "-")}${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                    className="hover:text-p_primary hover:underline"
                                  >
                                    {item.name}
                                  </Link>
                                </div>
                                <div className={cellClass} data-testid={item.name}>
                                  <Link
                                    to={`/zoodpay-money/balance-sheet/${item.name.toLowerCase().replace(/[()]/g, "").replace(/\s+/g, "-").replace(/\//g, "-")}${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                    className="hover:text-p_primary hover:underline"
                                  >
                                    {formatAfaAmount(item.balance)}
                                  </Link>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className={cellClass}>
                                  {item.name}
                                </div>
                                <div className={cellClass}>
                                  {formatAfaAmount(item.balance)}
                                </div>
                              </>
                            )}
                          </div>
                        )
                      )}
                      <div>
                        <div className={totalClass}>
                          <span>Total Liability</span>
                          <span data-testid="Total Liability">
                            {formatAfaAmount(balanceData.liabilities?.total)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="bg-white mt-1">
                        <div className={`${sectionClass} flex justify-between`}>
                          <div className={colHeaderClass}>Equity/Capital</div>
                          <div className={colHeaderClass}>Amount</div>
                        </div>
                        {balanceData.equity_capital?.details?.map(
                          (item: BalanceSheetItem, idx: number) => (
                            <div className="flex justify-between py-1" key={idx}>
                              {item.name === "Capital/Owner Equity" ? (
                                <>
                                  <div className={cellClass}>
                                    <Link
                                      to={`/zoodpay-money/balance-sheet/equity-capital/transactions${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                      className="hover:text-p_primary hover:underline"
                                    >
                                      {item.name}
                                    </Link>
                                  </div>
                                  <div className={cellClass} data-testid={item.name}>
                                    <Link
                                      to={`/zoodpay-money/balance-sheet/equity-capital/transactions${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                      className="hover:text-p_primary hover:underline"
                                    >
                                      {formatAfaAmount(item.balance)}
                                    </Link>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className={cellClass}>
                                    {item.name}
                                  </div>
                                  <div className={cellClass}>
                                    {formatAfaAmount(item.balance)}
                                  </div>
                                </>
                              )}
                            </div>
                          )
                        )}
                        <div>
                          <div className={totalClass}>
                            <span>Total Equity/Capital</span>
                            <span data-testid="Total Equity/Capital">
                              {formatAfaAmount(balanceData.equity_capital?.total)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div>
                        <div className={`${sectionClass} flex justify-between`}>
                          <div className={colHeaderClass}>Revenue</div>
                          <div className={colHeaderClass}>Amount</div>
                        </div>
                        {balanceData.revenue?.details?.map(
                          (item: BalanceSheetItem, idx: number) => (
                            <div className="flex justify-between py-1" key={idx}>
                              {item.name === "Etopup Service Fee Revenue" || item.name === "Bundle Activation Service Fee Revenue" ? (
                                <>
                                  <div className={cellClass}>
                                    <Link
                                      to={`/zoodpay-money/balance-sheet/${item.name.toLowerCase().replace(/[()]/g, "").replace(/\s+/g, "-").replace(/\//g, "-")}${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                      className="hover:text-p_primary hover:underline"
                                    >
                                      {item.name}
                                    </Link>
                                  </div>
                                  <div className={cellClass} data-testid={item.name}>
                                    <Link
                                      to={`/zoodpay-money/balance-sheet/${item.name.toLowerCase().replace(/[()]/g, "").replace(/\s+/g, "-").replace(/\//g, "-")}${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                      className="hover:text-p_primary hover:underline"
                                    >
                                      {formatAfaAmount(item.balance)}
                                    </Link>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className={cellClass}>
                                    {item.name}
                                  </div>
                                  <div className={cellClass}>
                                    {formatAfaAmount(item.balance)}
                                  </div>
                                </>
                              )}
                            </div>
                          )
                        )}
                        <div className={totalClass}>
                          <span>Total Revenue</span>
                          <span data-testid="Total Revenue">
                            {formatAfaAmount(balanceData.revenue?.total)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div>
                        <div className={`${sectionClass} flex justify-between`}>
                          <div className={colHeaderClass}>Expenses</div>
                          <div className={colHeaderClass}>Amount</div>
                        </div>
                        {balanceData.expenses?.details?.map(
                          (item: BalanceSheetItem, idx: number) => (
                            <div className="flex justify-between py-1" key={idx}>
                              {item.name === "Bank Charges" ? <><div className={cellClass}><Link
                                to={`/zoodpay-money/balance-sheet/${item.name.toLowerCase().replace(/[()]/g, "").replace(/\s+/g, "-").replace(/\//g, "-")}${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                className="hover:text-p_primary hover:underline"
                              >
                                {item.name}
                              </Link></div><div className={cellClass}>
                                <Link
                                  to={`/zoodpay-money/balance-sheet/${item.name.toLowerCase().replace(/[()]/g, "").replace(/\s+/g, "-").replace(/\//g, "-")}${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                  className="hover:text-p_primary hover:underline"
                                >
                                  {formatAfaAmount(item.balance)}
                                </Link>
                              </div></> : item.name === "Service Fee Expense" ? (
                                <>
                                  <div className={cellClass}>
                                    <Link
                                      to={`/zoodpay-money/balance-sheet/service-fee-expense${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                      className="hover:text-p_primary hover:underline"
                                    >
                                      {item.name}
                                    </Link>
                                  </div>
                                  <div className={cellClass}>
                                    <Link
                                      to={`/zoodpay-money/balance-sheet/service-fee-expense${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                      className="hover:text-p_primary hover:underline"
                                    >
                                      {formatAfaAmount(item.balance)}
                                    </Link>
                                  </div>
                                </>
                              ) : ((item.name?.toLowerCase()?.includes("etopup service fee expense") || item.name?.toLowerCase()?.includes("etop up service fee expense")) && !item.name?.toLowerCase()?.includes("bundle")) ? (
                                <>
                                  <div className={cellClass}>
                                    <Link
                                      to={`/zoodpay-money/balance-sheet/etopup-service-fee-expense/transactions${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                      className="hover:text-p_primary hover:underline"
                                    >
                                      {item.name}
                                    </Link>
                                  </div>
                                  <div className={cellClass}>
                                    <Link
                                      to={`/zoodpay-money/balance-sheet/etopup-service-fee-expense/transactions${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                      className="hover:text-p_primary hover:underline"
                                    >
                                      {formatAfaAmount(item.balance)}
                                    </Link>
                                  </div>
                                </>
                              ) : item.name === "General Expenses" || item.name === "General Expense" ? (
                                <>
                                  <div className={cellClass}>
                                    <Link
                                      to={`/zoodpay-money/balance-sheet/general-expense/transactions${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                      className="hover:text-p_primary hover:underline"
                                    >
                                      {item.name}
                                    </Link>
                                  </div>
                                  <div className={cellClass} data-testid={item.name}>
                                    <Link
                                      to={`/zoodpay-money/balance-sheet/general-expense/transactions${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                      className="hover:text-p_primary hover:underline"
                                    >
                                      {formatAfaAmount(item.balance)}
                                    </Link>
                                  </div>
                                </>
                              ) : item.name === "CVM Commission" || item.name === "CVM Commission Expenses" ? (
                                <>
                                  <div className={cellClass}>
                                    <Link
                                      to={`/zoodpay-money/balance-sheet/cvm-commission/transactions${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                      className="hover:text-p_primary hover:underline"
                                    >
                                      {item.name}
                                    </Link>
                                  </div>
                                  <div className={cellClass}>
                                    <Link
                                      to={`/zoodpay-money/balance-sheet/cvm-commission/transactions${searchParams.size !== 0 ? `?${searchParams}` : ""}`}
                                      className="hover:text-p_primary hover:underline"
                                    >
                                      {formatAfaAmount(item.balance)}
                                    </Link>
                                  </div>
                                </>
                              ) : <>
                              <div className={cellClass}>
                                {item.name}
                              </div>
                              <div className={cellClass} data-testid={item.name}>
                                {formatAfaAmount(item.balance)}
                              </div>
                            </>}
                            </div>
                          )
                        )}
                        <div className={totalClass}>
                          <span>Total Expenses</span>
                          <span data-testid="Total Expenses">
                            {formatAfaAmount(balanceData.expenses?.total)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className={`flex justify-between bg-positive mt-4 py-[2px]`}>
                        <div className={`${colHeaderClass} text-white`}>
                          Net Income
                        </div>
                        <div className={`${colHeaderClass} text-white`} data-testid="Net Income">
                          {formatAfaAmount(balanceData.net_income)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex sticky justify-around w-full items-start gap-8 pt-4">
                  <div className="w-1/2">
                    <div className="bg-p_primary flex justify-between text-white text-title1  px-3 py-2.5">
                      <span>Total Assets</span>
                      <span className="ml-4 text-heading6-semi" data-testid="Total Assets">
                        {formatAfaAmount(balanceData.assets?.total)}
                      </span>
                    </div>
                  </div>
                  <div className="w-1/2 flex flex-col gap-1">
                    <div className="bg-p_primary flex justify-between text-white text-title1  px-3 py-2.5">
                      Total Amount
                      <span className="ml-4 text-heading6-semi"  data-testid="Total Amount">
                        {formatAfaAmount(balanceData.total_amount)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end w-full gap-8">
                  <div className="w-1/2"></div>
                  <div className=" w-1/2 text-body2 truncate">Note : Total Amount = Liabilities + Equity or Capital + Revenue -
                    Expense</div>
                </div>
              </div>
            </div>
          </div>

        </>
      )}
    </div>
  );
}
